import React, { useState, useCallback } from 'react';
import api from '../services/api';

// The component declaration (Stage 7). One row per FRS fabric line — not per component — because
// an order can knit the same fabric in two shades, or two fabrics that both read as "body".
//
// This screen is deliberately small. It lists a GPO's FRS lines, shows which are declared, and
// lets someone declare, amend (with a reason) and sign. It adds no capability the KNP did not
// have; it is where the surviving half of the KNP goes.

const box = { border: '1px solid #e6e6e6', borderRadius: 8, background: '#fff' };
const th = { textAlign: 'left', padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#666',
             textTransform: 'uppercase', letterSpacing: '.04em', borderBottom: '1px solid #eee', whiteSpace: 'nowrap' };
const td = { padding: '9px 10px', fontSize: 12.5, borderBottom: '1px solid #f5f5f5', verticalAlign: 'top' };
const btn = (bg, fg) => ({ padding: '4px 10px', border: 'none', borderRadius: 5, background: bg,
                           color: fg, cursor: 'pointer', fontSize: 11, fontWeight: 600 });
const pill = (bg, fg) => ({ padding: '2px 8px', borderRadius: 8, fontSize: 10, fontWeight: 700, background: bg, color: fg });

export default function DeclarationsTab({ user }) {
  const [gpo, setGpo] = useState('');
  const [lines, setLines] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [trail, setTrail] = useState(null);

  const canSign = ['admin', 'management'].includes(user && user.role);

  const load = useCallback((g) => {
    if (!g) return;
    setBusy(true);
    api.get('/procurement/gpo-components?gpo_no=' + encodeURIComponent(g))
      .then(r => { setLines(r.data.lines || []); setLoaded(true); })
      .catch(e => window.alert(e.response?.data?.error || 'Could not load the order.'))
      .finally(() => setBusy(false));
  }, []);

  // THE GSM GOES ON AT DECLARATION (ruling 24). Until now this form posted four fields and no
  // spec at all, so every declaration made here carried a blank target — GPO 1743's three rows
  // are blank for exactly that reason — and every populated value in the table came from
  // migration 317's copy out of knp_components, which is where the 23 nonsense tolerances came
  // from. The tolerance defaults to the house ±5%; a figure typed in always wins.
  const declare = (line) => {
    const g = window.prompt(
      `Finished GSM for this fabric line${line.item_desc ? ` (${line.item_desc})` : ''}?\n\n`
      + 'Leave blank if it is not known yet — it can be amended later.',
      line.gsm ? String(line.gsm) : '');
    if (g === null) return;                        // cancelled, not "blank"
    const target = String(g).trim();
    if (target && !(Number(target) > 0)) return window.alert('GSM must be a positive number.');

    let tolerance = '';
    if (target) {
      const house = Math.round(Number(target) * 0.05 * 100) / 100;
      const t = window.prompt(
        `Tolerance for ${target} GSM?\n\n`
        + `The house standard is ±5%, which is ±${house}. Leave it as it stands to use that,\n`
        + 'or type the buyer\'s figure to override it.',
        String(house));
      if (t === null) return;
      tolerance = String(t).trim();
      if (tolerance && !(Number(tolerance) > 0)) return window.alert('Tolerance must be a positive number.');
      // Sent ONLY when it differs from the house figure, so the row records whether the number
      // is somebody's choice ('declared') or the default ('house_5pct'). Posting it back
      // unchanged would make every declaration look like a deliberate override.
      if (Number(tolerance) === house) tolerance = '';
    }

    api.post('/procurement/gpo-components', {
      gpo_no: gpo, frs_line_id: line.line_id,
      component: line.component || null, estimate_id: line.fabric_estimate_id || null,
      ...(target ? { gsm_target: Number(target) } : {}),
      ...(tolerance ? { gsm_tolerance: Number(tolerance) } : {}),
    }).then(() => load(gpo)).catch(e => window.alert(e.response?.data?.error || 'Failed'));
  };

  const setComponent = (d) => {
    const c = window.prompt('Component code (body, collar, cuff, rib, tape, placket, waistband, hood, pocket, facing, gusset, binding):', d.component || '');
    if (c === null) return;
    const why = window.prompt('Reason for this amendment (recorded against the declaration):', '');
    if (why === null) return;
    if (!why.trim()) return window.alert('A reason is required.');
    api.put('/procurement/gpo-components/' + d.id, { component: c.trim().toLowerCase(), reason: why })
      .then(() => load(gpo)).catch(e => window.alert(e.response?.data?.error || 'Failed'));
  };

  const sign = (d) => {
    if (!window.confirm(`Sign the declaration for ${d.component || 'this line'} on GPO ${gpo}?\n\nSigning is what permits Deroi to knit it.`)) return;
    api.patch('/procurement/gpo-components/' + d.id + '/sign', { remarks: '' })
      .then(() => load(gpo)).catch(e => window.alert(e.response?.data?.error || 'Failed'));
  };

  const unsign = (d) => {
    const why = window.prompt('Reason for withdrawing the signature (recorded):', '');
    if (why === null) return;
    if (!why.trim()) return window.alert('A reason is required.');
    api.patch('/procurement/gpo-components/' + d.id + '/unsign', { reason: why })
      .then(() => load(gpo)).catch(e => window.alert(e.response?.data?.error || 'Failed'));
  };

  const showTrail = (d) => {
    api.get('/procurement/gpo-components/' + d.id + '/amendments')
      .then(r => setTrail({ d, rows: r.data.data || [] })).catch(() => {});
  };

  return (
    <div>
      <div style={{ ...box, padding: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 12.5, color: '#555', lineHeight: 1.6, marginBottom: 12 }}>
          A declaration is <strong>one fabric line of one order</strong> — the same grain the FRS is
          written at, so two shades of one fabric are two declarations and two decisions.
          {' '}<strong>Signing is what permits Deroi to knit it</strong>: an unsigned declaration
          refuses a greige lot exactly as no declaration does.
        </div>
        <form onSubmit={e => { e.preventDefault(); load(gpo.trim()); }} style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={gpo} onChange={e => setGpo(e.target.value)} placeholder="GPO number, e.g. 1736"
                 style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 190 }} />
          <button type="submit" disabled={busy} style={{ ...btn('#3B3A8F', '#fff'), padding: '7px 16px', fontSize: 12.5 }}>
            {busy ? 'Loading…' : 'Open order'}
          </button>
        </form>
      </div>

      {loaded && !lines.length && (
        <div style={{ ...box, padding: 18, fontSize: 13, color: '#8a5a00', background: '#FFF8E6' }}>
          GPO {gpo} has no FRS fabric lines, so there is nothing to declare. The fabric sheet comes first.
        </div>
      )}

      {!!lines.length && (
        <div style={{ ...box, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 900 }}>
            <thead>
              <tr>
                <th style={th}>Fabric line</th><th style={th}>Shade</th><th style={th}>Qty (kg)</th>
                <th style={th}>Component</th><th style={th}>Estimate</th><th style={th}>Status</th><th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {lines.map(l => {
                const d = l.declaration;
                return (
                  <tr key={l.line_id}>
                    <td style={td}>
                      <div style={{ fontWeight: 600 }}>{l.item_desc || <em style={{ color: '#aaa' }}>unnamed</em>}</div>
                      <div style={{ fontSize: 10.5, color: '#999' }}>{l.fabric_type || '—'}</div>
                    </td>
                    <td style={td}>{l.shade || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {l.qty_req_kg ? Number(l.qty_req_kg).toFixed(3) : '—'}
                    </td>
                    <td style={td}>
                      {d
                        ? (d.component || <span style={pill('#FFF1E6', '#9A4A00')}>none</span>)
                        : (l.component
                            ? <span style={{ color: '#888' }}>{l.component} <span style={{ fontSize: 10 }}>(suggested)</span></span>
                            : <span style={pill('#FFF1E6', '#9A4A00')}>maps to nothing</span>)}
                    </td>
                    <td style={td}>
                      {d
                        ? (d.estimate_no
                            ? <span>{d.estimate_no} <span style={pill(d.estimate_status === 'approved' ? '#EAF7EE' : '#FFF1E6',
                                                                    d.estimate_status === 'approved' ? '#166534' : '#9A4A00')}>{d.estimate_status}</span></span>
                            : <span style={{ color: '#999' }}>none</span>)
                        : <span style={{ color: '#ccc' }}>—</span>}
                    </td>
                    <td style={td}>
                      {!d && <span style={pill('#f2f2f2', '#777')}>not declared</span>}
                      {d && d.rsr_approved_at && (
                        <span style={pill('#EAF7EE', '#166534')}>✓ signed</span>
                      )}
                      {d && !d.rsr_approved_at && <span style={pill('#FFF1E6', '#9A4A00')}>unsigned</span>}
                      {d && d.signed_by_name && (
                        <div style={{ fontSize: 10, color: '#999', marginTop: 3 }}>
                          {d.signed_by_name}, {String(d.rsr_approved_at).slice(0, 10)}
                        </div>
                      )}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {!d && <button onClick={() => declare(l)} style={btn('#3B3A8F', '#fff')}>Declare</button>}
                      {d && !d.rsr_approved_at && (
                        <>
                          <button onClick={() => setComponent(d)} style={{ ...btn('#EEEDF8', '#3B3A8F'), marginRight: 4 }}>Amend</button>
                          {canSign && <button onClick={() => sign(d)} style={btn('#166534', '#fff')}>Sign</button>}
                        </>
                      )}
                      {d && d.rsr_approved_at && canSign && (
                        <button onClick={() => unsign(d)} style={{ ...btn('#FCEBEB', '#A32D2D'), marginRight: 4 }}>Withdraw</button>
                      )}
                      {d && <button onClick={() => showTrail(d)} style={{ ...btn('transparent', '#666'), textDecoration: 'underline' }}>Trail</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {trail && (
        <div onClick={() => setTrail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 50 }}>
          <div onClick={e => e.stopPropagation()} style={{ ...box, maxWidth: 700, width: '100%', maxHeight: '80vh', overflow: 'auto', padding: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Amendment trail — {trail.d.component || 'line'} on GPO {trail.d.gpo_no}
            </div>
            {!trail.rows.length && <div style={{ fontSize: 12.5, color: '#888' }}>No amendments. The declaration is as it was created.</div>}
            {!!trail.rows.length && (
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead><tr><th style={th}>When</th><th style={th}>Field</th><th style={th}>From → to</th><th style={th}>Reason</th><th style={th}>By</th></tr></thead>
                <tbody>
                  {trail.rows.map(a => (
                    <tr key={a.id}>
                      <td style={td}>{String(a.changed_at).slice(0, 10)}</td>
                      <td style={td}>{a.changed_field}</td>
                      <td style={td}><span style={{ color: '#999' }}>{a.old_value || '—'}</span> → <strong>{a.new_value || '—'}</strong></td>
                      <td style={td}>{a.reason}</td>
                      <td style={td}>{a.changed_by_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ textAlign: 'right', marginTop: 12 }}>
              <button onClick={() => setTrail(null)} style={{ ...btn('#f0f2f8', '#333'), padding: '7px 18px', fontSize: 12.5 }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
